import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatIDR, staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Search, Check } from "lucide-react";

export const VALIDITY_OPTIONS = [
  { key: "never", label: "Never", hint: "No expiry date is ever applied." },
  { key: "on_date", label: "On Date", hint: "Expires at the end of the chosen day." },
  { key: "days_from_purchase", label: "Days from Purchase", hint: "Counts from the order payment date." },
  { key: "days_from_first_use", label: "Days from First Use", hint: "Counts from the first session the client claims." },
];

export const validityLabel = (t) => {
  const mode = t?.validity_mode || (t?.validity_days ? "days_from_purchase" : "never");
  if (mode === "never") return "Never expires";
  if (mode === "on_date") return t?.validity_date ? `On ${t.validity_date}` : "On date";
  if (mode === "days_from_first_use") return `${t?.validity_days || 0} days from first use`;
  return `${t?.validity_days || 0} days from purchase`;
};

export const listPriceOf = (row) =>
  Number(row?.pricing?.price ?? row?.price ?? 0) || 0;

const STEPS = ["Series Details", "Service or Package", "Branch Availability", "Quantity & Pricing"];

const emptyForm = {
  name: "",
  description: "",
  validity_mode: "never",
  validity_date: "",
  validity_days: 365,
  service_id: null,
  package_id: null,
  branch_ids: [],
  quantity: 3,
  sell_mode: "amount",
  discount_percent: 0,
  price: 0,
  active: true,
};

function StepRail({ step }) {
  return (
    <div className="flex items-center gap-1.5 mt-3" data-testid="series-wizard-rail">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const current = n === step;
        return (
          <div key={label} className="flex items-center gap-1.5 min-w-0">
            <div
              data-testid={`series-wizard-step-dot-${n}`}
              className={
                "h-6 w-6 rounded-full grid place-items-center text-[11px] font-semibold shrink-0 transition-colors " +
                (current ? "bg-primary text-primary-foreground"
                  : done ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground")
              }
            >
              {done ? <Check className="w-3 h-3" /> : n}
            </div>
            <span className={"text-[11px] truncate " + (current ? "text-foreground font-medium" : "text-muted-foreground")}>{label}</span>
            {n < STEPS.length && <span className="w-4 h-px bg-border mx-1 shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

function CategoryGroup({ title, rows, selectedId, onPick, testid }) {
  const [open, setOpen] = useState(false); // collapsed by default
  return (
    <div className="border-b border-border/60 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
        data-testid={`${testid}-toggle`}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        <span className="text-[11px] text-muted-foreground">({rows.length})</span>
        {rows.some((r) => r.id === selectedId) && (
          <Badge className="ml-auto h-5 px-2 text-[10px] rounded-full bg-primary/10 text-primary hover:bg-primary/10 border border-primary/20">Selected</Badge>
        )}
      </button>
      {open && (
        <div className="pb-1">
          {rows.map((r) => (
            <label
              key={r.id}
              className="flex items-center gap-2 px-3 py-1.5 pl-8 text-sm cursor-pointer hover:bg-muted/30"
              data-testid={`${testid}-row-${r.id}`}
            >
              <Checkbox
                checked={selectedId === r.id}
                onCheckedChange={(c) => onPick(c ? r.id : null)}
                data-testid={`${testid}-tick-${r.id}`}
              />
              <span className="flex-1 truncate">{r.name}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{formatIDR(listPriceOf(r))}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SeriesWizard({ open, onOpenChange, editing, services, categories, packages, branches, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState("services");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setQuery("");
    if (editing) {
      setForm({
        ...emptyForm,
        ...editing,
        validity_mode: editing.validity_mode || (editing.validity_days ? "days_from_purchase" : "never"),
        validity_date: editing.validity_date || "",
        validity_days: editing.validity_days || 365,
        branch_ids: editing.branch_ids || [],
        service_id: editing.service_id || null,
        package_id: editing.package_id || null,
        sell_mode: editing.sell_mode || "amount",
      });
      setTab(editing.package_id ? "packages" : "services");
    } else {
      setForm(emptyForm);
      setTab("services");
    }
  }, [open, editing]);

  // --- Step 2 data: live mirror of Brand Settings → Services ------------
  const catName = useMemo(() => {
    const m = {};
    (categories || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [categories]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const buckets = {};
    (services || [])
      .filter((s) => s.active !== false)
      .filter((s) => !q || (s.name || "").toLowerCase().includes(q))
      .forEach((s) => {
        const label = catName[s.category_id] || (s.category || "").trim() || "Uncategorized";
        (buckets[label] = buckets[label] || []).push(s);
      });
    return Object.entries(buckets)
      .map(([label, rows]) => [label, rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""))])
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [services, catName, query]);

  const visiblePackages = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (packages || [])
      .filter((p) => p.active !== false)
      .filter((p) => !q || (p.name || "").toLowerCase().includes(q));
  }, [packages, query]);

  // --- Step 4 pricing -------------------------------------------------
  const picked = useMemo(() => {
    if (form.package_id) return (packages || []).find((p) => p.id === form.package_id) || null;
    if (form.service_id) return (services || []).find((s) => s.id === form.service_id) || null;
    return null;
  }, [form.service_id, form.package_id, services, packages]);

  const unitPrice = picked ? listPriceOf(picked) : 0;
  const qty = Math.max(1, parseInt(form.quantity, 10) || 0);
  const totalPrice = Math.round(unitPrice * qty * 100) / 100;
  const sellPrice = form.sell_mode === "discount"
    ? Math.round(totalPrice * (1 - (Math.max(0, Math.min(100, Number(form.discount_percent) || 0)) / 100)) * 100) / 100
    : Math.max(0, Number(form.price) || 0);
  const perSession = qty > 0 ? Math.round((sellPrice / qty) * 100) / 100 : 0;

  const stepError = () => {
    if (step === 1) {
      if (!form.name.trim()) return "Series name is required.";
      if (form.validity_mode === "on_date" && !form.validity_date) return "Pick the expiry date.";
      if (["days_from_purchase", "days_from_first_use"].includes(form.validity_mode)
        && !(parseInt(form.validity_days, 10) > 0)) return "Enter the number of days (1 or more).";
      return null;
    }
    if (step === 2 && !form.service_id && !form.package_id) return "Pick one service or one package.";
    if (step === 3 && !(form.branch_ids || []).length) return "Select at least one branch.";
    if (step === 4) {
      if (qty < 1) return "Quantity must be at least 1.";
      if (form.sell_mode === "amount" && !(sellPrice > 0)) return "Enter the fixed sell price.";
      return null;
    }
    return null;
  };

  const next = () => {
    const err = stepError();
    if (err) return toast.error(err);
    setStep((s) => Math.min(4, s + 1));
  };

  const save = async () => {
    const err = stepError();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        service_id: form.service_id || null,
        package_id: form.package_id || null,
        branch_ids: form.branch_ids,
        quantity: qty,
        unit_price: unitPrice,
        sell_mode: form.sell_mode,
        discount_percent: form.sell_mode === "discount" ? Number(form.discount_percent) || 0 : 0,
        price: sellPrice,
        validity_mode: form.validity_mode,
        validity_date: form.validity_mode === "on_date" ? form.validity_date : null,
        validity_days: ["days_from_purchase", "days_from_first_use"].includes(form.validity_mode)
          ? parseInt(form.validity_days, 10) : null,
        active: !!form.active,
      };
      if (editing) await staffApi.put(`/series-templates/${editing.id}`, payload);
      else await staffApi.post("/series-templates", payload);
      toast.success(editing ? "Series updated" : "Series created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pickService = (id) => set({ service_id: id, package_id: null, price: 0 });
  const pickPackage = (id) => set({ package_id: id, service_id: null, price: 0 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="series-wizard">
        <DialogHeader>
          <DialogTitle data-testid="series-wizard-title">
            {editing ? "Edit series" : "New series"} · Step {step} of 4 — {STEPS[step - 1]}
          </DialogTitle>
          <DialogDescription className="text-xs">
            A series bundles ONE treatment or ONE package into pre-paid sessions. Client-specific, no surcharge on purchase.
          </DialogDescription>
          <StepRail step={step} />
        </DialogHeader>

        <div className="py-1 min-h-[320px] max-h-[55vh] overflow-y-auto pr-1">
          {/* ---------------- Step 1 — Series Details ---------------- */}
          {step === 1 && (
            <div className="space-y-4" data-testid="series-step-1">
              <div>
                <Label>Series Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="e.g. 5× Signature Facial"
                  data-testid="series-name"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  className="resize-none"
                  value={form.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="What the client gets, how it's used…"
                  data-testid="series-desc"
                />
              </div>
              <div>
                <Label>Validity</Label>
                <Select value={form.validity_mode} onValueChange={(v) => set({ validity_mode: v })}>
                  <SelectTrigger className="mt-1" data-testid="series-validity-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VALIDITY_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key} data-testid={`series-validity-opt-${o.key}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1" data-testid="series-validity-hint">
                  {VALIDITY_OPTIONS.find((o) => o.key === form.validity_mode)?.hint}
                </p>
                {form.validity_mode === "on_date" && (
                  <div className="mt-2">
                    <Label className="text-xs">Expires on</Label>
                    <Input
                      type="date"
                      value={form.validity_date || ""}
                      onChange={(e) => set({ validity_date: e.target.value })}
                      data-testid="series-validity-date"
                    />
                  </div>
                )}
                {["days_from_purchase", "days_from_first_use"].includes(form.validity_mode) && (
                  <div className="mt-2">
                    <Label className="text-xs">
                      Days {form.validity_mode === "days_from_purchase" ? "from purchase" : "from first use"}
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.validity_days}
                      onChange={(e) => set({ validity_days: e.target.value })}
                      data-testid="series-validity-days"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!form.active} onCheckedChange={(v) => set({ active: v })} data-testid="series-active" />
                <Label className="mb-0">Active</Label>
              </div>
            </div>
          )}

          {/* ---------------- Step 2 — Service or Package ---------------- */}
          {step === 2 && (
            <div className="space-y-3" data-testid="series-step-2">
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-full border border-border p-0.5 bg-muted/40">
                  {[["services", "Services"], ["packages", "Packages"]].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setTab(k)}
                      data-testid={`series-tab-${k}`}
                      className={
                        "px-3 h-7 rounded-full text-xs font-medium transition-colors " +
                        (tab === k ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={tab === "services" ? "Search services…" : "Search packages…"}
                    className="pl-8 h-8 text-sm"
                    data-testid="series-item-search"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                One series covers exactly ONE treatment or ONE package — ticking a new row replaces the previous pick.
              </p>

              {tab === "services" ? (
                <div className="rounded-md border border-border overflow-hidden" data-testid="series-services-list">
                  {grouped.length === 0 && (
                    <p className="text-xs text-muted-foreground p-4 text-center">No services match.</p>
                  )}
                  {grouped.map(([label, rows]) => (
                    <CategoryGroup
                      key={label}
                      title={label}
                      rows={rows}
                      selectedId={form.service_id}
                      onPick={pickService}
                      testid={`series-cat-${label.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-border overflow-hidden" data-testid="series-packages-list">
                  {visiblePackages.length === 0 && (
                    <p className="text-xs text-muted-foreground p-4 text-center">No packages match.</p>
                  )}
                  {visiblePackages.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30 border-b border-border/60 last:border-b-0"
                      data-testid={`series-package-row-${p.id}`}
                    >
                      <Checkbox
                        checked={form.package_id === p.id}
                        onCheckedChange={(c) => pickPackage(c ? p.id : null)}
                        data-testid={`series-package-tick-${p.id}`}
                      />
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{formatIDR(listPriceOf(p))}</span>
                    </label>
                  ))}
                </div>
              )}

              {picked && (
                <p className="text-xs" data-testid="series-step-2-selected">
                  Selected: <span className="font-medium">{picked.name}</span> · {formatIDR(listPriceOf(picked))}
                </p>
              )}
            </div>
          )}

          {/* ---------------- Step 3 — Branch Availability ---------------- */}
          {step === 3 && (
            <div className="space-y-3" data-testid="series-step-3">
              <p className="text-xs text-muted-foreground">
                Tick every branch where this series can be SOLD and REDEEMED. At least one is required.
              </p>
              <div className="rounded-md border border-border overflow-hidden" data-testid="series-branch-list">
                {(branches || []).map((b) => {
                  const on = (form.branch_ids || []).includes(b.id);
                  return (
                    <label
                      key={b.id}
                      className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30 border-b border-border/60 last:border-b-0"
                      data-testid={`series-branch-row-${b.id}`}
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={(c) => set({
                          branch_ids: c
                            ? [...(form.branch_ids || []), b.id]
                            : (form.branch_ids || []).filter((x) => x !== b.id),
                        })}
                        data-testid={`series-branch-tick-${b.id}`}
                      />
                      <span className="flex-1 truncate">{b.name}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => set({ branch_ids: (branches || []).map((b) => b.id) })}
                  data-testid="series-branch-all">Select all</Button>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => set({ branch_ids: [] })}
                  data-testid="series-branch-none">Clear</Button>
                <span className="ml-auto text-xs text-muted-foreground self-center" data-testid="series-branch-count">
                  {(form.branch_ids || []).length} of {(branches || []).length} selected
                </span>
              </div>
            </div>
          )}

          {/* ---------------- Step 4 — Quantity & Pricing ---------------- */}
          {step === 4 && (
            <div className="space-y-4" data-testid="series-step-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.quantity}
                    onChange={(e) => set({ quantity: e.target.value })}
                    data-testid="series-qty"
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input readOnly value={formatIDR(unitPrice)} className="bg-muted/50" data-testid="series-unit-price" />
                </div>
                <div>
                  <Label>Total Price</Label>
                  <Input readOnly value={formatIDR(totalPrice)} className="bg-muted/50" data-testid="series-total-price" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Unit Price mirrors {form.package_id ? "the package price in Brand Catalog → Packages" : "the service price in Brand Settings → Services"}
                {picked ? ` (${picked.name})` : ""}.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sell Price</Label>
                  <Select value={form.sell_mode} onValueChange={(v) => set({ sell_mode: v })}>
                    <SelectTrigger className="mt-1" data-testid="series-sell-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discount" data-testid="series-sell-mode-discount">Discount</SelectItem>
                      <SelectItem value="amount" data-testid="series-sell-mode-amount">Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.sell_mode === "discount" ? (
                  <div>
                    <Label>Discount (%)</Label>
                    <Input
                      type="number" min={0} max={100}
                      value={form.discount_percent}
                      onChange={(e) => set({ discount_percent: e.target.value })}
                      data-testid="series-discount-percent"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Fixed sell price (IDR)</Label>
                    <Input
                      type="number" min={0}
                      value={form.price}
                      onChange={(e) => set({ price: e.target.value })}
                      data-testid="series-fixed-amount"
                    />
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Sell Price</span>
                  <span className="text-xl font-semibold tabular-nums" data-testid="series-sell-price">{formatIDR(sellPrice)}</span>
                </div>
                <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                  <span>Per session on the order page</span>
                  <span className="tabular-nums" data-testid="series-per-session">{formatIDR(perSession)}</span>
                </div>
                {form.sell_mode === "discount" && totalPrice > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="series-discount-note">
                    {formatIDR(totalPrice)} − {Number(form.discount_percent) || 0}% = {formatIDR(sellPrice)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground" data-testid="series-validity-summary">
                  Validity: {validityLabel({ ...form })}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="series-wizard-cancel">Cancel</Button>
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} data-testid="series-wizard-back">Back</Button>
          )}
          {step < 4
            ? <Button onClick={next} data-testid="series-wizard-next">Next</Button>
            : <Button onClick={save} disabled={saving} data-testid="series-wizard-save">{saving ? "Saving…" : "Save series"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
